/**
 * 健康管理 - 主页面
 * 集成打卡、趋势、方案、设置四大模块
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import {
  Scale, Ruler, Droplets, Activity, Target,
  CheckCircle, Circle, Heart, AlertCircle, Home, BarChart2,
  BookOpen, Settings, Plus, Minus,
  Bell, ChevronRight, ChevronLeft, ChevronDown,
  Pencil, Trash2, Calendar, Menu, ClipboardList
} from 'lucide-react';
import {
  getProfile,
  updateProfile,
  getDailyRecords,
  saveDailyRecord,
  getActivities,
  getActivitiesInRange,
  createActivity,
  updateActivity,
  deleteActivity,
  resetActivities,
  getActivityCompletions,
  saveActivityCompletion
} from '../services/healthApi';
import './HealthHome.css';

const formatDate = (d) => d.toISOString().split('T')[0];
const formatDisplayDate = (str) => {
  const d = new Date(str);
  return `${d.getMonth() + 1}月${d.getDate()}日`;
};
const WEEKDAY_LABELS = ['日', '一', '二', '三', '四', '五', '六'];
const formatWeekday = (str) => {
  const d = new Date(str + 'T12:00:00');
  return WEEKDAY_LABELS[d.getDay()];
};
const getWeekDates = (mondayStr) => {
  const base = new Date(mondayStr + 'T12:00:00');
  const out = [];
  for (let i = 0; i < 7; i++) {
    const x = new Date(base);
    x.setDate(base.getDate() + i);
    out.push(formatDate(x));
  }
  return out;
};

const calculateBMI = (weight, height) => {
  if (!weight || !height) return 0;
  const h = height / 100;
  return Number((weight / (h * h)).toFixed(1));
};

const calculateWaistHipRatio = (waist, hip) => {
  if (!waist || !hip) return 0;
  return Number((waist / hip).toFixed(2));
};

const getBMICategory = (bmi) => {
  if (bmi < 18.5) return { category: '偏瘦', color: '#3b82f6' };
  if (bmi < 24) return { category: '正常', color: '#22c55e' };
  if (bmi < 28) return { category: '超重', color: '#eab308' };
  return { category: '肥胖', color: '#ef4444' };
};

function HealthHome() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [profile, setProfile] = useState({
    name: '用户',
    age: 0,
    height: 170,
    currentWeight: 0,
    targetWeight: 0,
    startDate: formatDate(new Date()),
    doctorName: '',
    hospital: ''
  });
  const [dailyRecords, setDailyRecords] = useState([]);
  const [todayRecord, setTodayRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [_saving, setSaving] = useState(false);
  const [saveToast, setSaveToast] = useState(false);
  const [reminders, setReminders] = useState({
    medication: true,
    exercise: true,
    followUp: true
  });
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' && window.innerWidth < 768
  );
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() =>
    typeof window !== 'undefined' && window.innerWidth < 768
  );
  const [sidebarOverlayOpen, setSidebarOverlayOpen] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const fn = () => setIsMobile(mq.matches);
    mq.addEventListener('change', fn);
    return () => mq.removeEventListener('change', fn);
  }, []);
  const getMonday = (d) => {
    const x = new Date(d);
    const day = x.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    x.setDate(x.getDate() + diff);
    return formatDate(x);
  };
  const [calendarWeekStart, setCalendarWeekStart] = useState(() =>
    getMonday(new Date())
  );
  const [calendarDetailDate, setCalendarDetailDate] = useState(null);
  const [trendRange, setTrendRange] = useState(14); // 7|14|30|90 天
  const [checkInTargetDate, setCheckInTargetDate] = useState(null); // null=今天
  // 打卡活动（懒加载，仅进入活动 tab 时拉取）
  const [activities, setActivities] = useState([]);
  const [activitiesLoading, setActivitiesLoading] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [activityFormOpen, setActivityFormOpen] = useState(false);
  const getDefaultEndDate = () => {
    const d = new Date();
    d.setMonth(d.getMonth() + 3);
    return formatDate(d);
  };
  const [activityFormData, setActivityFormData] = useState({
    name: '',
    startDate: formatDate(new Date()),
    endDate: getDefaultEndDate()
  });
  const [activitySaving, setActivitySaving] = useState(false);
  const [editDateRange, setEditDateRange] = useState(false);
  const [editDateRangeVal, setEditDateRangeVal] = useState({ start: '', end: '' });
  // Dashboard 当日活动（懒加载，仅 dashboard 时拉取）
  const [activitiesInRange, setActivitiesInRange] = useState([]);
  const [activityCompletions, setActivityCompletions] = useState([]);
  const [activityFold, setActivityFold] = useState({});
  const [itemNotesDraft, setItemNotesDraft] = useState({});
  const [historyActivities, setHistoryActivities] = useState([]);
  const [historyCompletions, setHistoryCompletions] = useState([]);
  const [historyRecordExpand, setHistoryRecordExpand] = useState({});

  const TRACKING_METRICS = [
    { type: 'weight', label: '体重', unit: 'kg' },
    { type: 'waist', label: '腰围', unit: 'cm' },
    { type: 'hip', label: '臀围', unit: 'cm' },
    { type: 'ketones', label: '尿酮', unit: '' },
    { type: 'waterIntake', label: '饮水量', unit: 'ml' }
  ];

  const TIME_SLOTS = [
    '早晨', '早餐前', '早餐', '早餐后', '上午',
    '午餐前', '午餐', '午餐后', '中午', '下午',
    '晚餐前', '晚餐', '晚餐后', '晚上', '睡前'
  ];
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [profileRes, recordsRes] = await Promise.all([
        getProfile(),
        getDailyRecords({ limit: 120 })
      ]);
      if (profileRes?.success && profileRes?.data) {
        const p = profileRes.data;
        setProfile((prev) => ({ ...prev, ...p }));
        if (p.reminders && typeof p.reminders === 'object') {
          setReminders({
            medication: p.reminders.medication !== false,
            exercise: p.reminders.exercise !== false,
            followUp: p.reminders.followUp !== false
          });
        }
      }
      if (recordsRes?.success && Array.isArray(recordsRes?.data)) {
        setDailyRecords(recordsRes.data);
        const today = formatDate(new Date());
        const todayData = recordsRes.data.find((r) => r.date === today);
        setTodayRecord(todayData || null);
      }
    } catch (e) {
      console.error('健康数据加载失败:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (activeTab === 'stats' || activeTab === 'trends') {
      getDailyRecords({ limit: 120 }).then((res) => {
        if (res?.success && Array.isArray(res?.data)) {
          setDailyRecords(res.data);
          const today = formatDate(new Date());
          setTodayRecord(res.data.find((r) => r.date === today) || null);
        }
      });
    }
  }, [activeTab]);

  // 活动 tab 懒加载：仅进入活动 tab 时拉取
  useEffect(() => {
    if (activeTab !== 'activities') return;
    let cancelled = false;
    setActivitiesLoading(true);
    getActivities()
      .then((res) => {
        if (!cancelled && res?.success && Array.isArray(res?.data)) {
          setActivities(res.data);
        }
      })
      .finally(() => {
        if (!cancelled) setActivitiesLoading(false);
      });
    return () => { cancelled = true; };
  }, [activeTab]);

  // 切换活动时重置编辑时间区间状态
  useEffect(() => {
    if (selectedActivity?._id) setEditDateRange(false);
  }, [selectedActivity?._id]);

  // 历史打卡记录：加载活动及完成记录（按日期范围，dashboard/stats 共用）
  useEffect(() => {
    if ((activeTab !== 'dashboard' && activeTab !== 'stats') ||
        !dailyRecords?.length) return;
    const dates = dailyRecords.map((r) => r.date);
    const from = dates[0];
    const to = dates[dates.length - 1];
    let cancelled = false;
    Promise.all([
      getActivities(),
      getActivityCompletions({ from, to })
    ]).then(([actRes, compRes]) => {
      if (cancelled) return;
      if (actRes?.success && Array.isArray(actRes?.data)) {
        setHistoryActivities(actRes.data);
      }
      if (compRes?.success && Array.isArray(compRes?.data)) {
        setHistoryCompletions(compRes.data);
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [activeTab, dailyRecords]);

  // Dashboard 活动懒加载：仅 dashboard 时拉取当日活动及完成记录
  useEffect(() => {
    if (activeTab !== 'dashboard') return;
    const date = checkInTargetDate || formatDate(new Date());
    let cancelled = false;
    Promise.all([
      getActivitiesInRange(date),
      getActivityCompletions({ date })
    ])
      .then(([rangeRes, compRes]) => {
        if (cancelled) return;
        if (rangeRes?.success && Array.isArray(rangeRes?.data)) {
          setActivitiesInRange(rangeRes.data);
        }
        if (compRes?.success && Array.isArray(compRes?.data)) {
          setActivityCompletions(compRes.data);
        }
      })
      .catch(() => { if (!cancelled) setActivitiesInRange([]); });
    return () => { cancelled = true; };
  }, [activeTab, checkInTargetDate]);

  const isDateInRange = (date, startDate, endDate) =>
    date && startDate && endDate && date >= startDate && date <= endDate;
  const getTargetDate = () =>
    checkInTargetDate || formatDate(new Date());
  const activeRecord = (() => {
    const target = getTargetDate();
    return dailyRecords.find((r) => r.date === target) || null;
  })();
  const updateDailyRecord = async (updates, forDate) => {
    const target = forDate || getTargetDate();
    const rec = dailyRecords.find((r) => r.date === target) || null;
    const base = { date: target, ...rec, ...updates };
    if (!rec) base.waterIntake = base.waterIntake ?? 0;
    const newRecords = [...dailyRecords];
    const idx = newRecords.findIndex((r) => r.date === target);
    if (idx >= 0) newRecords[idx] = base;
    else newRecords.push(base);
    newRecords.sort((a, b) => (a.date || '').localeCompare(b.date || ''));
    setDailyRecords(newRecords);
    const today = formatDate(new Date());
    if (target === today) setTodayRecord(base);
    try {
      setSaving(true);
      await saveDailyRecord(base);
      setSaveToast(true);
      setTimeout(() => setSaveToast(false), 2000);
    } catch (e) {
      console.error('保存失败:', e);
    } finally {
      setSaving(false);
    }
  };
  const updateTodayRecord = (updates) => updateDailyRecord(updates, null);

  const saveProfileUpdate = async (next) => {
    setProfile(next);
    try {
      await updateProfile(next);
    } catch (e) {
      console.error('档案更新失败:', e);
    }
  };

  const currentWeight = todayRecord?.weight ?? profile.currentWeight ?? 0;
  const latestWithWaist = [...dailyRecords].reverse().find((r) => r.waist != null);
  const latestWithHip = [...dailyRecords].reverse().find((r) => r.hip != null);
  const currentWaist = todayRecord?.waist ?? latestWithWaist?.waist ?? profile.baseline?.waist;
  const currentHip = todayRecord?.hip ?? latestWithHip?.hip ?? profile.baseline?.hip;
  const bmi = calculateBMI(currentWeight, profile.height);
  const waistHipRatio = calculateWaistHipRatio(currentWaist, currentHip);
  const bmiCategory = getBMICategory(bmi);

  const totalWeightLoss = (profile.currentWeight || 0) - currentWeight;
  const weightToGo = currentWeight - (profile.targetWeight || 0);
  const progressPercent = Math.min(
    100,
    Math.max(
      0,
      (profile.currentWeight && profile.targetWeight)
        ? (totalWeightLoss / (profile.currentWeight - profile.targetWeight)) * 100
        : 0
    )
  );

  const getItemsForDate = (dateStr) => {
    const rec = dailyRecords.find((r) => r.date === dateStr);
    const comps = (historyCompletions || []).filter((c) => c.date === dateStr);
    const acts = (historyActivities || []).filter((a) =>
      isDateInRange(dateStr, a.startDate, a.endDate)
    );
    const activities = acts.map((act) => {
      const comp = comps.find((c) => String(c.activityId) === String(act._id));
      const items = (act.items || []).map((it, i) => {
        const ci = comp?.items?.find((c) => c.itemIndex === i);
        return {
          name: it.name,
          timeSlot: it.timeSlot || it.time,
          dosage: it.dosage,
          completed: !!ci?.completed
        };
      });
      return { name: act.name, items };
    });
    return {
      weight: rec?.weight,
      ketones: rec?.ketones,
      waterIntake: rec?.waterIntake ?? 0,
      activities
    };
  };
  const activityStats = (() => {
    const targetDate = checkInTargetDate || formatDate(new Date());
    const inRange = activitiesInRange.filter((a) =>
      targetDate >= (a.startDate || '') && targetDate <= (a.endDate || '')
    );
    let total = 0;
    let done = 0;
    inRange.forEach((act) => {
      const items = act.items || [];
      const metrics = act.metrics || [];
      const comp = activityCompletions.find(
        (c) => String(c.activityId) === String(act._id)
      );
      items.forEach((_, i) => {
        total += 1;
        if (comp?.items?.find((ci) => ci.itemIndex === i)?.completed) done += 1;
      });
      metrics.forEach((m) => {
        total += 1;
        const isDone = m.type === 'weight' ? activeRecord?.weight != null
          : m.type === 'waist' ? activeRecord?.waist != null
          : m.type === 'hip' ? activeRecord?.hip != null
          : m.type === 'ketones' ? !!activeRecord?.ketones
          : m.type === 'waterIntake' ? (activeRecord?.waterIntake || 0) >= 2000
          : false;
        if (isDone) done += 1;
      });
    });
    return { total, done };
  })();
  const activeTotalCoreTasks = activityStats.total;
  const activeCompletedCount = activityStats.done;
  const completionPercent = activeTotalCoreTasks
    ? Math.round((activeCompletedCount / activeTotalCoreTasks) * 100)
    : 0;

  const getRecordCompletion = (r, completionsForDate) => {
    if (completionsForDate?.length && historyActivities?.length) {
      let totalItems = 0;
      let doneItems = 0;
      historyActivities.forEach((act) => {
        if (!isDateInRange(r.date, act.startDate, act.endDate)) return;
        const comp = completionsForDate.find(
          (c) => String(c.activityId) === String(act._id)
        );
        const items = act.items || [];
        totalItems += items.length;
        if (comp?.items) {
          doneItems += comp.items.filter((ci) => ci.completed).length;
        }
      });
      if (totalItems > 0) {
        const pct = Math.round((doneItems / totalItems) * 100);
        return { coreDone: doneItems, coreTotal: totalItems, pct };
      }
    }
    return { coreDone: 0, coreTotal: 0, pct: 0 };
  };
  const recordMap = (dailyRecords || []).reduce((acc, r) => {
    const compsForDate = (historyCompletions || []).filter(
      (c) => c.date === r.date
    );
    const { pct } = getRecordCompletion(
      r,
      compsForDate.length ? compsForDate : undefined
    );
    acc[r.date] = pct;
    return acc;
  }, {});
  const getTrendData = () => {
    const sorted = [...(dailyRecords || [])].sort((a, b) =>
      (a.date || '').localeCompare(b.date || '')
    );
    return sorted.slice(-trendRange).map((r) => ({
      date: formatDisplayDate(r.date),
      weight: r.weight != null ? Number(r.weight) : undefined,
      waist: r.waist != null ? Number(r.waist) : undefined,
      hip: r.hip != null ? Number(r.hip) : undefined,
      bmi: r.weight != null ? calculateBMI(Number(r.weight), profile.height) : 0
    }));
  };

  if (loading) {
    return (
      <div className="health-page" style={{ padding: '2rem', textAlign: 'center' }}>
        加载中...
      </div>
    );
  }

  const navItems = [
    { id: 'dashboard', label: '打卡', icon: Home },
    { id: 'stats', label: '统计', icon: Calendar },
    { id: 'trends', label: '趋势', icon: BarChart2 },
    { id: 'activities', label: '活动', icon: ClipboardList },
    { id: 'care', label: '方案', icon: BookOpen },
    { id: 'settings', label: '设置', icon: Settings }
  ];

  const renderDashboard = () => (
    <div style={{ padding: '0 1rem' }}>
      <div className="health-header">
        <div className="health-header-row">
          <div>
            <h2 className="health-header-title">你好，{profile.name}</h2>
            <p className="health-header-sub">
              减肥第{' '}
              {Math.max(
                1,
                Math.floor(
                  (new Date() - new Date(profile.startDate || profile.start)) / (1000 * 60 * 60 * 24)
                ) + 1
              )}{' '}
              天
            </p>
          </div>
          <div className="health-header-bell">
            <Bell size={24} />
          </div>
        </div>
        <div className="health-progress-row">
          <div className="health-progress-ring">
            <svg width="64" height="64" viewBox="0 0 64 64" style={{ transform: 'rotate(-90deg)' }}>
              <circle cx="32" cy="32" r="28" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="6" />
              <circle
                cx="32"
                cy="32"
                r="28"
                fill="none"
                stroke="white"
                strokeWidth="6"
                strokeDasharray={`${completionPercent * 1.76} 176`}
                strokeLinecap="round"
              />
            </svg>
            <div
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <span className="health-progress-text">{completionPercent}%</span>
            </div>
          </div>
          <div>
            <p className="health-header-sub">今日任务完成</p>
            <p className="health-progress-text">
              {activeCompletedCount}/{activeTotalCoreTasks}
            </p>
          </div>
        </div>
        <div className="health-stats-row">
          <div>
            <p className="health-stat-label">当前体重</p>
            <p className="health-stat-value">{Number(currentWeight).toFixed(1)}kg</p>
          </div>
          <div>
            <p className="health-stat-label">目标</p>
            <p className="health-stat-value">{profile.targetWeight || '--'}kg</p>
          </div>
        </div>
      </div>

      <div className="health-card health-card-checkin">
        <div className="health-card-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <h3 className="health-card-title">打卡任务</h3>
            <div className="health-checkin-date-tabs">
              <button
                type="button"
                className={!checkInTargetDate ? 'active' : ''}
                onClick={() => setCheckInTargetDate(null)}
              >
                今天
              </button>
              <button
                type="button"
                className={checkInTargetDate === formatDate(new Date(Date.now() - 864e5)) ? 'active' : ''}
                onClick={() => {
                  const d = new Date();
                  d.setDate(d.getDate() - 1);
                  setCheckInTargetDate(formatDate(d));
                }}
              >
                昨日
              </button>
              {checkInTargetDate && checkInTargetDate !== formatDate(new Date()) && checkInTargetDate !== formatDate(new Date(Date.now() - 864e5)) && (
                <span className="health-checkin-date-custom" title={checkInTargetDate}>
                  {formatDisplayDate(checkInTargetDate)}
                </span>
              )}
            </div>
          </div>
          <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>
            {activeCompletedCount}/{activeTotalCoreTasks} 完成
          </span>
        </div>
        <div className="health-checkin-body" style={{ padding: '0 1rem 1rem' }}>
          {activitiesInRange.length === 0 ? (
            <div
              style={{
                padding: '2rem 1rem',
                textAlign: 'center',
                color: '#9ca3af',
                fontSize: '0.9rem'
              }}
            >
              <ClipboardList size={48} style={{ marginBottom: 12, opacity: 0.5 }} />
              <p>暂无打卡活动</p>
              <p style={{ marginTop: 4 }}>请到「活动」tab 创建打卡活动</p>
              <button
                type="button"
                className="health-water-btn"
                style={{ marginTop: 16 }}
                onClick={() => setActiveTab('activities')}
              >
                去创建活动
              </button>
            </div>
          ) : (
            activitiesInRange.map((act) => {
              const comp = activityCompletions.find(
                (c) => String(c.activityId) === String(act._id)
              );
              const items = act.items || [];
              const metrics = act.metrics || [];
              const compItems = comp?.items || [];
              const allComplete = items.length > 0 && items.every((_, i) =>
                compItems.find((ci) => ci.itemIndex === i)?.completed
              );
              const isFolded = activityFold[act._id] ?? allComplete;
              const setFolded = (v) =>
                setActivityFold((prev) => ({ ...prev, [act._id]: v }));
              const toggleFold = () => setFolded(!isFolded);
              const toggleItem = async (itemIdx) => {
                const curr = compItems.find((ci) => ci.itemIndex === itemIdx);
                const nextCompleted = !(curr?.completed);
                const nextItems = items.map((_, i) => ({
                  itemIndex: i,
                  completed: i === itemIdx ? nextCompleted : (
                    compItems.find((ci) => ci.itemIndex === i)?.completed || false
                  ),
                  notes: (compItems.find((ci) => ci.itemIndex === i)?.notes || '')
                }));
                // 乐观更新：先更新 UI 再异步保存，点击后立即看到状态变化
                const targetDate = getTargetDate();
                const newComp = {
                  activityId: act._id,
                  date: targetDate,
                  items: nextItems,
                  recordedAt: new Date().toISOString()
                };
                setActivityCompletions((prev) => {
                  const rest = prev.filter(
                    (c) => String(c.activityId) !== String(act._id)
                  );
                  return [...rest, newComp];
                });
                // 同步更新 historyCompletions，让下方打卡记录统计即时生效
                setHistoryCompletions((prev) => {
                  const rest = prev.filter(
                    (c) => !(c.date === targetDate && String(c.activityId) === String(act._id))
                  );
                  return [...rest, newComp];
                });
                try {
                  await saveItemCompletion(nextItems);
                } catch (e) {
                  console.error('活动打卡保存失败:', e);
                  const oldComp = activityCompletions.find(
                    (c) => String(c.activityId) === String(act._id)
                  );
                  // 保存失败时回滚 activityCompletions 和 historyCompletions
                  setActivityCompletions((prev) => {
                    const rest = prev.filter(
                      (c) => String(c.activityId) !== String(act._id)
                    );
                    return oldComp ? [...rest, oldComp] : rest;
                  });
                  setHistoryCompletions((prev) => {
                    const rest = prev.filter(
                      (c) => !(c.date === targetDate && String(c.activityId) === String(act._id))
                    );
                    const oldHist = historyCompletions.find(
                      (c) => c.date === targetDate && String(c.activityId) === String(act._id)
                    );
                    return oldHist ? [...rest, oldHist] : rest;
                  });
                }
              };
              const updateItemNotes = async (itemIdx, notes) => {
                const nextItems = items.map((_, i) => ({
                  itemIndex: i,
                  completed: compItems.find((ci) => ci.itemIndex === i)?.completed || false,
                  notes: i === itemIdx ? notes : (
                    compItems.find((ci) => ci.itemIndex === i)?.notes || ''
                  )
                }));
                await saveItemCompletion(nextItems);
              };
              const saveItemCompletion = async (nextItems) => {
                const targetDate = getTargetDate();
                try {
                  const res = await saveActivityCompletion({
                    activityId: act._id,
                    date: targetDate,
                    items: nextItems
                  });
                  if (res?.success && res?.data) {
                    const data = res.data;
                    setActivityCompletions((prev) => {
                      const rest = prev.filter(
                        (c) => String(c.activityId) !== String(act._id)
                      );
                      return [...rest, data];
                    });
                    // 同步到 historyCompletions，下方打卡记录统计与后端一致
                    setHistoryCompletions((prev) => {
                      const rest = prev.filter(
                        (c) => !(c.date === targetDate && String(c.activityId) === String(act._id))
                      );
                      return [...rest, data];
                    });
                    setSaveToast(true);
                    setTimeout(() => setSaveToast(false), 1500);
                  }
                } catch (e) {
                  console.error('活动打卡保存失败:', e);
                }
              };
              const doneCount = items.filter((_, i) =>
                compItems.find((ci) => ci.itemIndex === i)?.completed
              ).length;
              const metricsDone = metrics.filter((m) => {
                if (m.type === 'weight') return activeRecord?.weight != null;
                if (m.type === 'waist') return activeRecord?.waist != null;
                if (m.type === 'hip') return activeRecord?.hip != null;
                if (m.type === 'ketones') return !!activeRecord?.ketones;
                if (m.type === 'waterIntake') return (activeRecord?.waterIntake || 0) >= 2000;
                return false;
              }).length;
              const totalDone = doneCount + metricsDone;
              const totalTasks = items.length + metrics.length;
              return (
                <div
                  key={act._id}
                  className="health-activity-block"
                  style={{
                    marginBottom: 12,
                    background: '#f9fafb',
                    borderRadius: 8,
                    overflow: 'hidden'
                  }}
                >
                  <div
                    className="health-group-header health-group-header-toggle health-activity-header"
                    onClick={toggleFold}
                    style={{
                      padding: '10px 12px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8
                    }}
                  >
                    {isFolded ? (
                      <ChevronRight size={18} color="#6b7280" />
                    ) : (
                      <ChevronDown size={18} color="#6b7280" />
                    )}
                    <span className="health-item-name" style={{ flex: 1, fontSize: '0.9rem' }}>
                      {act.name}
                    </span>
                    <span style={{ fontSize: '0.8rem', color: '#9ca3af' }}>
                      {totalDone}/{totalTasks} 完成
                    </span>
                  </div>
                  {!isFolded && (
                  <div className="health-activity-content" style={{ padding: '0 12px 12px 40px' }}>
                  {metrics.length > 0 && (
                    <div className="health-activity-metrics-wrap" style={{ marginBottom: 12 }}>
                      {metrics.map((m) => (
                        <div key={m.type} style={{ marginBottom: 8 }}>
                          {m.type === 'weight' && (
                            <div className="health-task">
                              <p className="health-item-name">空腹体重</p>
                              <div className="health-input-row">
                                <input
                                  type="number"
                                  step="0.1"
                                  placeholder="输入体重(kg)"
                                  className="health-input"
                                  value={activeRecord?.weight ?? ''}
                                  onChange={(e) =>
                                    updateTodayRecord({
                                      weight: parseFloat(e.target.value) || undefined
                                    })
                                  }
                                />
                                <button
                                  className="health-btn-sm"
                                  onClick={() =>
                                    updateTodayRecord({
                                      weight: (activeRecord?.weight || 70) - 0.1
                                    })
                                  }
                                >
                                  <Minus size={16} />
                                </button>
                                <button
                                  className="health-btn-sm"
                                  onClick={() =>
                                    updateTodayRecord({
                                      weight: (activeRecord?.weight || 70) + 0.1
                                    })
                                  }
                                >
                                  <Plus size={16} />
                                </button>
                              </div>
                            </div>
                          )}
                          {m.type === 'waist' && (
                            <div className="health-task health-task-inline-row">
                              <p className="health-item-name">腰围 (cm)</p>
                              <input
                                type="number"
                                className="health-input health-input-sm"
                                placeholder="腰围"
                                value={activeRecord?.waist ?? ''}
                                onChange={(e) =>
                                  updateTodayRecord({
                                    waist: parseFloat(e.target.value) || undefined
                                  })
                                }
                                style={{ width: 80 }}
                              />
                            </div>
                          )}
                          {m.type === 'hip' && (
                            <div className="health-task health-task-inline-row">
                              <p className="health-item-name">臀围 (cm)</p>
                              <input
                                type="number"
                                className="health-input health-input-sm"
                                placeholder="臀围"
                                value={activeRecord?.hip ?? ''}
                                onChange={(e) =>
                                  updateTodayRecord({
                                    hip: parseFloat(e.target.value) || undefined
                                  })
                                }
                                style={{ width: 80 }}
                              />
                            </div>
                          )}
                          {m.type === 'ketones' && (
                            <div className="health-task">
                              <p className="health-item-name">尿酮监测</p>
                              <div className="health-ketone-btns">
                                {['-', '+/-', '1+', '2+', '3+', '4+'].map((k) => (
                                  <button
                                    key={k}
                                    className={`health-ketone-btn ${activeRecord?.ketones === k ? 'active' : ''}`}
                                    onClick={() => updateTodayRecord({ ketones: k })}
                                  >
                                    {k}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                          {m.type === 'waterIntake' && (
                            <div className="health-task">
                              <p className="health-item-name">饮水量 (ml)</p>
                              <div className="health-water-btns">
                                {[250, 500, 1000].map((ml) => (
                                  <button
                                    key={ml}
                                    className="health-water-btn"
                                    onClick={() =>
                                      updateTodayRecord({
                                        waterIntake:
                                          (activeRecord?.waterIntake || 0) + ml
                                      })
                                    }
                                  >
                                    +{ml}ml
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  {items.map((it, idx) => {
                    const ci = compItems.find((c) => c.itemIndex === idx);
                    const done = ci?.completed || false;
                    const draftKey = `${act._id}-${idx}`;
                    const notes = itemNotesDraft[draftKey] ?? (ci?.notes ?? '');
                    return (
                      <div
                        key={idx}
                        className="health-item-with-notes health-checkin-item-card"
                        style={{
                          marginBottom: 12,
                          padding: 12,
                          background: '#fff',
                          borderRadius: 8,
                          border: '1px solid #e5e7eb',
                          boxShadow: '0 1px 2px rgba(0,0,0,0.04)'
                        }}
                      >
                        <div
                          className="health-item-main health-checkin-tappable-row"
                          role="button"
                          tabIndex={0}
                          onClick={() => toggleItem(idx)}
                          onKeyDown={(e) => e.key === 'Enter' && toggleItem(idx)}
                          title={done ? '已打卡，点击取消' : '点击打卡'}
                          aria-label={done ? '已打卡' : '点击打卡'}
                        >
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <p className="health-item-name" style={{ fontSize: '0.9rem', marginBottom: 2 }}>
                              {it.name}
                              {it.timeSlot && (
                                <span style={{ color: '#6b7280', marginLeft: 6, fontWeight: 400 }}>
                                  {it.timeSlot}
                                </span>
                              )}
                            </p>
                            {it.dosage && (
                              <p className="health-item-meta" style={{ fontSize: '0.8rem' }}>
                                {it.dosage}
                              </p>
                            )}
                          </div>
                          <div className="health-check-btn" aria-hidden="true">
                            {done ? (
                              <CheckCircle size={22} color="#22c55e" />
                            ) : (
                              <Circle size={22} color="#d1d5db" />
                            )}
                          </div>
                        </div>
                        <input
                          type="text"
                          className="health-notes-input"
                          placeholder="备注（可选）"
                          value={notes}
                          onChange={(e) =>
                            setItemNotesDraft((p) => ({
                              ...p,
                              [draftKey]: e.target.value
                            }))
                          }
                          onBlur={(e) => {
                            const v = e.target.value;
                            if (v !== (ci?.notes ?? '')) {
                              updateItemNotes(idx, v);
                            }
                            setItemNotesDraft((p) => {
                              const next = { ...p };
                              delete next[draftKey];
                              return next;
                            });
                          }}
                        />
                      </div>
                    );
                  })}
                  </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      <button
        className="health-link-btn"
        onClick={() => setActiveTab('trends')}
      >
        <div className="health-task-info">
          <BarChart2 size={24} color="#9ca3af" />
          <span className="health-task-name">查看详细趋势数据</span>
        </div>
        <ChevronRight size={20} color="#9ca3af" />
      </button>

      <div className="health-card">
        <div className="health-card-header">
          <h3 className="health-card-title">打卡记录</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>
              共 {dailyRecords.length} 天
            </span>
            <button
              type="button"
              className="health-more-btn"
              onClick={() => setActiveTab('stats')}
            >
              更多
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
        <div style={{ maxHeight: 360, overflowY: 'auto' }}>
          {(dailyRecords || []).slice().reverse().map((r) => {
            const compsForDate = (historyCompletions || []).filter(
              (c) => c.date === r.date
            );
            const { coreDone, coreTotal, pct } = getRecordCompletion(
              r,
              compsForDate.length ? compsForDate : undefined
            );
            const isToday = r.date === formatDate(new Date());
            const expanded = historyRecordExpand[r.date];
            const actsForDate = historyActivities.filter((a) =>
              isDateInRange(r.date, a.startDate, a.endDate)
            );
            const hasActivityData = actsForDate.length > 0;
            return (
              <div
                key={r.date}
                className="health-record-item"
                style={{
                  flexDirection: 'column',
                  alignItems: 'stretch',
                  background: isToday ? '#f0fdf4' : undefined,
                  padding: 0
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '0.75rem 1rem',
                    cursor: hasActivityData ? 'pointer' : undefined
                  }}
                  onClick={() =>
                    hasActivityData &&
                    setHistoryRecordExpand((p) => ({
                      ...p,
                      [r.date]: !p[r.date]
                    }))
                  }
                >
                  <div>
                    <p className="health-item-name">
                      {formatDisplayDate(r.date)}
                      {isToday && (
                        <span style={{ marginLeft: 8, fontSize: 12, color: '#10b981' }}>
                          今天
                        </span>
                      )}
                    </p>
                    <p className="health-item-meta">
                      体重{r.weight != null ? `${r.weight}kg` : '-'} · 
                      任务 {coreDone}/{coreTotal} ({pct}%)
                    </p>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    {hasActivityData && (
                      expanded ? (
                        <ChevronDown size={16} color="#9ca3af" />
                      ) : (
                        <ChevronRight size={16} color="#9ca3af" />
                      )
                    )}
                    <div
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        background: pct === 100 ? '#22c55e' :
                          pct >= 60 ? '#fbbf24' : '#e5e7eb',
                        color: pct === 100 ? 'white' : '#6b7280',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 'bold',
                        fontSize: 14
                      }}
                    >
                      {pct}%
                    </div>
                  </div>
                </div>
                {hasActivityData && expanded && (
                  <div
                    style={{
                      padding: '0 1rem 0.75rem 1rem',
                      borderTop: '1px solid #f3f4f6',
                      background: '#fafafa'
                    }}
                  >
                    {actsForDate.map((act) => {
                      const comp = compsForDate.find(
                        (c) => String(c.activityId) === String(act._id)
                      );
                      const items = act.items || [];
                      const compItems = comp?.items || [];
                      const done = items.filter((_, i) =>
                        compItems.find((ci) => ci.itemIndex === i)?.completed
                      ).length;
                      const total = items.length;
                      return (
                        <div
                          key={act._id}
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '4px 0',
                            fontSize: '0.8rem',
                            color: '#4b5563'
                          }}
                        >
                          <span className="health-item-name" style={{ fontWeight: 500 }}>
                            {act.name}
                          </span>
                          <span style={{ color: '#6b7280' }}>
                            {done}/{total} 完成
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  const renderTrends = () => {
    const data = getTrendData();
    const baselineW = profile.baseline?.weight != null ? Number(profile.baseline.weight) : null;
    const baselineDate = profile.baseline?.recordedAt || '';
    const weights = data.map((d) => d.weight).filter(Boolean);
    const targetW = profile.targetWeight || 0;
    const allWForRange = [...weights, targetW || 0, baselineW || 0].filter((v) => v > 0);
    const maxW = allWForRange.length
      ? Math.max(...allWForRange, 1)
      : Math.max(targetW || 1, baselineW || 1, 1);
    const minW = allWForRange.length
      ? Math.min(...allWForRange, maxW - 1)
      : Math.min(targetW || 0, baselineW || 0, maxW - 1) || maxW - 1 || 1;
    const range = maxW - minW || 1;
    const targetH = targetW && targetW >= minW && targetW <= maxW
      ? Math.max(3, ((targetW - minW) / range) * 100)
      : null;
    const baselineH = baselineW != null && baselineW >= minW && baselineW <= maxW
      ? Math.max(3, ((baselineW - minW) / range) * 100)
      : null;

    return (
      <div style={{ padding: '0 1rem', paddingBottom: '5rem' }}>
        <div className="health-trend-card">
          <div
            className="health-card-header"
            style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #f3f4f6' }}
          >
            <h3 className="health-card-title" style={{ margin: 0 }}>体重趋势</h3>
            <div className="health-trend-range-btns">
              {[7, 14, 30, 90].map((n) => (
                <button
                  key={n}
                  type="button"
                  className={`health-trend-range-btn ${trendRange === n ? 'active' : ''}`}
                  onClick={() => setTrendRange(n)}
                >
                  {n}天
                </button>
              ))}
            </div>
          </div>
          <div
            style={{
              position: 'relative',
              display: 'flex',
              alignItems: 'flex-end',
              gap: '4px',
              height: 160,
              padding: '1rem 0.5rem 0'
            }}
          >
            {targetH != null && targetH > 0 && targetH < 100 && (
              <div
                className="health-trend-target-line"
                style={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  bottom: `${targetH}%`,
                  height: 0,
                  borderTop: '2px dashed #10b981',
                  zIndex: 1
                }}
                title={`目标 ${targetW}kg`}
              />
            )}
            {baselineH != null && baselineH > 0 && baselineH < 100 && (
              <div
                style={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  bottom: `${baselineH}%`,
                  height: 0,
                  borderTop: '2px dashed #6366f1',
                  zIndex: 1
                }}
                title={`基线 ${baselineDate}: ${baselineW}kg`}
              />
            )}
            {data.map((d, idx) => {
              const w = d.weight != null ? Number(d.weight) : null;
              const h = w != null ? ((w - minW) / range) * 100 : 0;
              return (
                <div
                  key={idx}
                  style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center'
                  }}
                >
                  <div
                    style={{
                      width: '100%',
                      maxWidth: 16,
                      height: w != null ? `${Math.max(h, 5)}%` : '0%',
                      background:
                        w != null && w <= (targetW || Infinity)
                          ? '#22c55e'
                          : '#34d399',
                      borderTopLeftRadius: 4,
                      borderTopRightRadius: 4
                    }}
                    title={w != null ? `${d.date}: ${w.toFixed(1)}kg${targetW ? ` · 目标${targetW}kg` : ''}` : `${d.date}: 未记录`}
                  />
                  <span style={{ fontSize: 10, color: '#9ca3af', marginTop: 4 }}>
                    {d.date?.slice(-3)}
                  </span>
                </div>
              );
            })}
          </div>
          <div style={{ fontSize: 11, margin: '0.25rem 0.5rem 0', textAlign: 'right', display: 'flex', gap: '1rem', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            {targetW > 0 && (
              <span style={{ color: '#10b981' }}>目标线 {targetW}kg</span>
            )}
            {baselineW != null && (
              <span style={{ color: '#6366f1' }}>
                基线 {baselineDate || '—'} {baselineW}kg
              </span>
            )}
          </div>
        </div>

        <div className="health-trend-card">
          <h3 className="health-card-title" style={{ marginBottom: '1rem' }}>腰围 / 臀围趋势</h3>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 100 }}>
            {(() => {
              const tData = getTrendData();
              const allV = tData.flatMap((x) => [x.waist, x.hip]).filter((v) => v != null && v > 0);
              const maxV = allV.length ? Math.max(...allV, 80) : 80;
              const minV = allV.length ? Math.min(...allV, 60) : 60;
              const range = maxV - minV || 1;
              return tData.map((d, idx) => {
                const waist = d.waist;
                const hip = d.hip;
                const h1 = waist ? ((waist - minV) / range) * 80 : 0;
                const h2 = hip ? ((hip - minV) / range) * 80 : 0;
                return (
                  <div key={idx} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                    <div style={{ display: 'flex', gap: 2, alignItems: 'flex-end', height: 80 }}>
                      {waist != null && (
                        <div
                          style={{
                            width: '50%',
                            height: `${Math.max(h1, 4)}%`,
                            background: '#8b5cf6',
                            borderRadius: '2px 2px 0 0',
                            minHeight: 4
                          }}
                          title={`腰围 ${waist}cm`}
                        />
                      )}
                      {hip != null && (
                        <div
                          style={{
                            width: '50%',
                            height: `${Math.max(h2, 4)}%`,
                            background: '#f97316',
                            borderRadius: '2px 2px 0 0',
                            minHeight: 4
                          }}
                          title={`臀围 ${hip}cm`}
                        />
                      )}
                    </div>
                    <span style={{ fontSize: 9, color: '#9ca3af' }}>{d.date?.slice(-2)}</span>
                  </div>
                );
              });
            })()}
          </div>
          <div style={{ display: 'flex', gap: '1rem', marginTop: 8, fontSize: 11, color: '#6b7280' }}>
            <span><i style={{ background: '#8b5cf6', width: 8, height: 8, display: 'inline-block', borderRadius: 2, marginRight: 4, verticalAlign: 'middle' }} /> 腰围</span>
            <span><i style={{ background: '#f97316', width: 8, height: 8, display: 'inline-block', borderRadius: 2, marginRight: 4, verticalAlign: 'middle' }} /> 臀围</span>
          </div>
          {(profile.baseline?.waist != null || profile.baseline?.hip != null) && (
            <p style={{ fontSize: 11, marginTop: 4, color: '#6366f1' }}>
              基线{baselineDate ? ` ${baselineDate}` : ''}:{' '}
              {profile.baseline?.waist != null && `腰围 ${profile.baseline.waist}cm`}
              {profile.baseline?.waist != null && profile.baseline?.hip != null && ' / '}
              {profile.baseline?.hip != null && `臀围 ${profile.baseline.hip}cm`}
            </p>
          )}
        </div>

        <div className="health-metric-grid">
          <div className="health-metric-item">
            <div className="health-task-info" style={{ marginBottom: 8 }}>
              <Scale size={16} color="#10b981" />
              <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>当前体重</span>
            </div>
            <p style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>
              {Number(currentWeight).toFixed(1)} <span style={{ fontSize: '0.875rem', fontWeight: 'normal' }}>kg</span>
            </p>
            <p className="health-task-desc">
              目标: {profile.targetWeight || '—'}kg
              {profile.baseline?.weight != null && (
                <span style={{ marginLeft: 8 }}>
                  · 基线{baselineDate ? ` ${baselineDate}` : ''}: {profile.baseline.weight}kg
                  {currentWeight != null && (
                    <span style={{ marginLeft: 4, color: (currentWeight - profile.baseline.weight) <= 0 ? '#10b981' : '#ef4444' }}>
                      (较基线 {(currentWeight - profile.baseline.weight) >= 0 ? '+' : ''}{(currentWeight - profile.baseline.weight).toFixed(1)}kg)
                    </span>
                  )}
                </span>
              )}
            </p>
          </div>
          <div className="health-metric-item">
            <div className="health-task-info" style={{ marginBottom: 8 }}>
              <Activity size={16} color="#3b82f6" />
              <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>BMI</span>
            </div>
            <p style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>{bmi}</p>
            <p style={{ fontSize: '0.75rem', color: bmiCategory.color || '#6b7280' }}>{bmiCategory.category}</p>
          </div>
          <div className="health-metric-item">
            <div className="health-task-info" style={{ marginBottom: 8 }}>
              <Ruler size={16} color="#8b5cf6" />
              <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>腰围</span>
            </div>
            <p style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>{currentWaist || '--'} cm</p>
            {profile.baseline?.waist != null && currentWaist != null && (
              <p className="health-task-desc">
                基线{baselineDate ? ` ${baselineDate}` : ''}: {profile.baseline.waist}cm
                <span style={{ marginLeft: 4, color: (currentWaist - profile.baseline.waist) <= 0 ? '#10b981' : '#ef4444' }}>
                  (较基线 {(currentWaist - profile.baseline.waist) >= 0 ? '+' : ''}{(currentWaist - profile.baseline.waist).toFixed(0)}cm)
                </span>
              </p>
            )}
          </div>
          <div className="health-metric-item">
            <div className="health-task-info" style={{ marginBottom: 8 }}>
              <Target size={16} color="#f97316" />
              <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>腰臀比</span>
            </div>
            <p style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>{waistHipRatio ? waistHipRatio.toFixed(2) : '--'}</p>
            {waistHipRatio > 0 && (
              <p style={{ fontSize: '0.75rem', color: waistHipRatio < 0.9 ? '#22c55e' : '#ef4444' }}>
                {waistHipRatio < 0.9 ? '正常' : '偏高'}
              </p>
            )}
            {profile.baseline?.waist != null && profile.baseline?.hip != null && profile.baseline.hip > 0 && (
              <p className="health-task-desc">
                基线{baselineDate ? ` ${baselineDate}` : ''}: {(profile.baseline.waist / profile.baseline.hip).toFixed(2)}
              </p>
            )}
          </div>
        </div>

        <div className="health-trend-card">
          <h3 className="health-card-title" style={{ marginBottom: '1rem' }}>减肥进度</h3>
          <div style={{ marginBottom: '1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <span>总体进度</span>
              <span style={{ color: '#10b981', fontWeight: 500 }}>{progressPercent.toFixed(1)}%</span>
            </div>
            <div className="health-progress-bar">
              <div
                className="health-progress-fill"
                style={{
                  width: `${progressPercent}%`,
                  background: 'linear-gradient(90deg, #10b981, #0d9488)'
                }}
              />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div style={{ background: '#ecfdf5', borderRadius: 8, padding: '1rem', textAlign: 'center' }}>
              <p style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#10b981' }}>
                {totalWeightLoss.toFixed(1)}
              </p>
              <p className="health-task-desc">已减(kg)</p>
            </div>
            <div style={{ background: '#fff7ed', borderRadius: 8, padding: '1rem', textAlign: 'center' }}>
              <p style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#f97316' }}>
                {weightToGo.toFixed(1)}
              </p>
              <p className="health-task-desc">待减(kg)</p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderStats = () => {
    const totalDays = dailyRecords.length;
    const fullDays = dailyRecords.filter((r) => getRecordCompletion(r).pct === 100).length;
    const partialDays = dailyRecords.filter((r) => {
      const p = getRecordCompletion(r).pct;
      return p >= 60 && p < 100;
    }).length;
    const lowDays = dailyRecords.filter((r) => getRecordCompletion(r).pct < 60).length;
    const avgPct = totalDays
      ? Math.round(
          dailyRecords.reduce((sum, r) => sum + getRecordCompletion(r).pct, 0) / totalDays
        )
      : 0;
    const last7 = dailyRecords.slice(-7);
    const last30 = dailyRecords.slice(-30);
    const avgPct7 = last7.length
      ? Math.round(last7.reduce((s, r) => s + getRecordCompletion(r).pct, 0) / last7.length)
      : 0;
    const avgPct30 = last30.length
      ? Math.round(last30.reduce((s, r) => s + getRecordCompletion(r).pct, 0) / last30.length)
      : 0;
    let streak = 0;
    for (let i = 0; i < 365; i++) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      const dateStr = formatDate(d);
      if (recordMap[dateStr] === undefined) break;
      streak++;
    }
    return (
      <div style={{ padding: '0 1rem', paddingBottom: '5rem' }}>
        <div className="health-card">
          <h3 className="health-card-title" style={{ padding: '1rem 1.25rem' }}>
            整体统计
          </h3>
          <div className="health-stats-overview">
            <div className="health-stat-item">
              <span className="health-stat-item-value">{totalDays}</span>
              <span className="health-stat-item-label">总打卡天数</span>
            </div>
            <div className="health-stat-item">
              <span className="health-stat-item-value">{fullDays}</span>
              <span className="health-stat-item-label">完美打卡</span>
            </div>
            <div className="health-stat-item">
              <span className="health-stat-item-value">{avgPct}%</span>
              <span className="health-stat-item-label">平均完成率</span>
            </div>
            <div className="health-stat-item">
              <span className="health-stat-item-value">{streak}</span>
              <span className="health-stat-item-label">连续打卡</span>
            </div>
          </div>
          <div className="health-stats-detail" style={{ padding: '0 1rem 1rem', borderTop: '1px solid #f3f4f6' }}>
            <p className="health-task-desc" style={{ margin: '0.75rem 0 0.5rem' }}>完成率分布</p>
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', fontSize: '0.8rem' }}>
              <span><i style={{ background: '#22c55e', width: 8, height: 8, borderRadius: 2, display: 'inline-block', marginRight: 4, verticalAlign: 'middle' }} /> 100%: {fullDays}天</span>
              <span><i style={{ background: '#fbbf24', width: 8, height: 8, borderRadius: 2, display: 'inline-block', marginRight: 4, verticalAlign: 'middle' }} /> 60%+: {partialDays}天</span>
              <span><i style={{ background: '#e5e7eb', width: 8, height: 8, borderRadius: 2, display: 'inline-block', marginRight: 4, verticalAlign: 'middle' }} /> &lt;60%: {lowDays}天</span>
            </div>
            <p className="health-task-desc" style={{ margin: '0.75rem 0 0.5rem' }}>近期趋势</p>
            <div style={{ display: 'flex', gap: '1rem', fontSize: '0.8rem' }}>
              <span>近7天平均: <strong style={{ color: '#10b981' }}>{avgPct7}%</strong></span>
              <span>近30天平均: <strong style={{ color: '#10b981' }}>{avgPct30}%</strong></span>
            </div>
          </div>
        </div>

        <div className="health-card">
          <div className="health-card-header">
            <h3 className="health-card-title">
              <Calendar size={20} style={{ verticalAlign: 'middle', marginRight: 6 }} />
              打卡日历
            </h3>
            <div className="health-calendar-nav">
              <button
                type="button"
                className="health-calendar-nav-btn"
                onClick={() => {
                  const d = new Date(calendarWeekStart + 'T12:00:00');
                  d.setDate(d.getDate() - 7);
                  setCalendarWeekStart(formatDate(d));
                }}
              >
                <ChevronLeft size={18} />
              </button>
              <span className="health-calendar-month">
                {(() => {
                  const m1 = new Date(calendarWeekStart + 'T12:00:00');
                  const m2 = new Date(m1);
                  m2.setDate(m2.getDate() + 6);
                  const sameMonth = m1.getMonth() === m2.getMonth();
                  return sameMonth
                    ? `${m1.getMonth() + 1}月${m1.getDate()}日-${m2.getDate()}日`
                    : `${m1.getMonth() + 1}月${m1.getDate()}日-${m2.getMonth() + 1}月${m2.getDate()}日`;
                })()}
              </span>
              <button
                type="button"
                className="health-calendar-nav-btn"
                onClick={() => {
                  const d = new Date(calendarWeekStart + 'T12:00:00');
                  d.setDate(d.getDate() + 7);
                  setCalendarWeekStart(formatDate(d));
                }}
              >
                <ChevronRight size={18} />
              </button>
              <button
                type="button"
                className="health-calendar-today-btn"
                onClick={() => {
                  setCalendarWeekStart(getMonday(new Date()));
                  setActiveTab('dashboard');
                }}
              >
                回到今天
              </button>
            </div>
          </div>
          <div className="health-calendar-week-wrap">
            {getWeekDates(calendarWeekStart).map((dateStr) => {
              const items = getItemsForDate(dateStr);
              const rec = dailyRecords.find((r) => r.date === dateStr);
              const pct = recordMap[dateStr];
              const hasRecord = pct !== undefined;
              const isToday = dateStr === formatDate(new Date());
              const d = new Date(dateStr + 'T12:00:00');
              const dayNum = d.getDate();
              const wd = formatWeekday(dateStr);
              let level = '';
              if (hasRecord) {
                level = pct === 100 ? 'full' : pct >= 60 ? 'partial' : 'low';
              }
              return (
                <div
                  key={dateStr}
                  role={hasRecord || isToday ? 'button' : undefined}
                  tabIndex={hasRecord || isToday ? 0 : undefined}
                  className={`health-calendar-week-day ${isToday ? 'today' : ''} ${level ? `level-${level}` : ''} ${hasRecord || isToday ? 'clickable' : ''}`}
                  onClick={() => {
                    if (hasRecord && rec) setCalendarDetailDate(dateStr);
                    else if (isToday) setActiveTab('dashboard');
                  }}
                >
                  <div className="health-calendar-week-header">
                    <span className="health-calendar-week-date">
                      {dayNum}日 {wd}
                      {isToday && <span className="health-calendar-week-today">今</span>}
                    </span>
                    {hasRecord && (
                      <span className="health-calendar-week-pct">{pct}%</span>
                    )}
                  </div>
                  <div className="health-calendar-week-body">
                    <div className="health-calendar-week-item">
                      <Scale size={14} />
                      <span>{items.weight != null ? `${items.weight}kg` : '-'}</span>
                      {items.weight != null && <CheckCircle size={14} color="#22c55e" />}
                    </div>
                    <div className="health-calendar-week-item">
                      <Droplets size={14} />
                      <span>{items.ketones || '-'}</span>
                      {items.ketones && <CheckCircle size={14} color="#22c55e" />}
                    </div>
                    <div className="health-calendar-week-item">
                      <Droplets size={14} />
                      <span>{(items.waterIntake || 0)}ml</span>
                      {(items.waterIntake || 0) >= 2000 && (
                        <CheckCircle size={14} color="#22c55e" />
                      )}
                    </div>
                    {(items.activities || []).map((act, ai) => (
                      <div key={ai} className="health-calendar-week-group">
                        <div className="health-calendar-week-group-title">
                          <ClipboardList size={14} /> {act.name}
                          {act.items.filter((i) => i.completed).length}/{act.items.length}
                        </div>
                        {act.items.map((it, i) => (
                          <div key={i} className="health-calendar-week-task">
                            <span className="health-calendar-week-task-name" title={it.name}>
                              {(it.timeSlot || '')?.slice(0, 2)}{it.name?.slice(0, 6)}
                            </span>
                            {it.completed ? (
                              <CheckCircle size={12} color="#22c55e" />
                            ) : (
                              <Circle size={12} color="#d1d5db" />
                            )}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="health-calendar-legend">
            <span><i style={{ background: '#22c55e' }} /> 100%</span>
            <span><i style={{ background: '#fbbf24' }} /> 60%+</span>
            <span><i style={{ background: '#e5e7eb' }} /> 部分</span>
          </div>

          {calendarDetailDate && (
            <div
              className="health-calendar-detail-backdrop"
              onClick={() => setCalendarDetailDate(null)}
            />
          )}
          {calendarDetailDate && (() => {
            const r = dailyRecords.find((rec) => rec.date === calendarDetailDate);
            if (!r) return null;
            const compsForDate = (historyCompletions || []).filter(
              (c) => c.date === calendarDetailDate
            );
            const { coreDone, coreTotal, pct } = getRecordCompletion(
              r,
              compsForDate.length ? compsForDate : undefined
            );
            const actsForDate = historyActivities.filter((a) =>
              isDateInRange(calendarDetailDate, a.startDate, a.endDate)
            );
            return (
              <div className="health-calendar-detail">
                <div className="health-calendar-detail-header">
                  <span>{formatDisplayDate(calendarDetailDate)}</span>
                  <span className="health-calendar-detail-pct">{pct}%</span>
                  <button
                    type="button"
                    className="health-calendar-detail-close"
                    onClick={() => setCalendarDetailDate(null)}
                  >
                    ×
                  </button>
                </div>
                <div className="health-calendar-detail-body">
                  <div className="health-calendar-detail-row">
                    <span>体重</span>
                    <span>{r.weight != null ? `${r.weight} kg` : '未记录'}</span>
                  </div>
                  <div className="health-calendar-detail-row">
                    <span>尿酮</span>
                    <span>{r.ketones || '未记录'}</span>
                  </div>
                  <div className="health-calendar-detail-row">
                    <span>饮水量</span>
                    <span>{(r.waterIntake || 0)} ml</span>
                  </div>
                  {actsForDate.map((act) => {
                    const comp = compsForDate.find(
                      (c) => String(c.activityId) === String(act._id)
                    );
                    const items = act.items || [];
                    const compItems = comp?.items || [];
                    const done = items.filter((_, i) =>
                      compItems.find((ci) => ci.itemIndex === i)?.completed
                    ).length;
                    return (
                      <div key={act._id} className="health-calendar-detail-row">
                        <span>{act.name}</span>
                        <span>{done}/{items.length}</span>
                      </div>
                    );
                  })}
                  <div className="health-calendar-detail-row" style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid #e5e7eb' }}>
                    <span>任务完成</span>
                    <span>{coreDone}/{coreTotal}</span>
                  </div>
                </div>
                <div style={{ padding: '0.75rem 1.25rem', borderTop: '1px solid #f3f4f6' }}>
                  <button
                    type="button"
                    className="health-calendar-detail-edit-btn"
                    onClick={() => {
                      setCheckInTargetDate(calendarDetailDate);
                      setCalendarDetailDate(null);
                      setActiveTab('dashboard');
                    }}
                  >
                    编辑此日
                  </button>
                </div>
              </div>
            );
          })()}
        </div>
      </div>
    );
  };

  const renderCarePlan = () => (
    <div style={{ padding: '0 1rem', paddingBottom: '5rem' }}>
      <div className="health-card">
        <div className="health-card-header">
          <div className="health-task-info">
            <div className="health-task-icon blue">
              <Heart size={28} />
            </div>
            <div>
              <p className="health-card-title">主诊医生</p>
              <p className="health-task-desc">{profile.doctorName || '未设置'}</p>
            </div>
          </div>
        </div>
        <div style={{ padding: '1rem', background: '#f9fafb', borderRadius: 8, margin: '0 1rem 1rem' }}>
          <p style={{ color: '#4b5563' }}>{profile.hospital || '未设置'}</p>
        </div>
      </div>
      <div className="health-card">
        <h3 className="health-card-title" style={{ padding: '1rem 1.25rem' }}>身体成分分析</h3>
        <div style={{ padding: '0 1rem 1rem' }}>
          <div className="health-item-row">
            <span style={{ color: '#6b7280' }}>当前体重</span>
            <span style={{ fontWeight: 'bold' }}>{Number(currentWeight).toFixed(1)} kg</span>
          </div>
          <div className="health-item-row">
            <span style={{ color: '#6b7280' }}>目标体重</span>
            <span style={{ fontWeight: 'bold', color: '#10b981' }}>{profile.targetWeight} kg</span>
          </div>
          <div className="health-item-row">
            <span style={{ color: '#6b7280' }}>还需减重</span>
            <span style={{ fontWeight: 'bold', color: '#f97316' }}>{weightToGo.toFixed(1)} kg</span>
          </div>
          <div className="health-item-row">
            <span style={{ color: '#6b7280' }}>BMI</span>
            <span style={{ fontWeight: 'bold' }}>{bmi} ({bmiCategory.category})</span>
          </div>
          {(profile.baseline?.weight || profile.baseline?.bodyFatPct != null) && (
            <>
              <div className="health-item-row" style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid #e5e7eb' }}>
                <span style={{ color: '#6b7280' }}>基线体重</span>
                <span>{profile.baseline?.weight != null ? `${profile.baseline.weight} kg` : '-'}</span>
              </div>
              <div className="health-item-row">
                <span style={{ color: '#6b7280' }}>基线体脂率</span>
                <span>{profile.baseline?.bodyFatPct != null ? `${profile.baseline.bodyFatPct}%` : '-'}</span>
              </div>
            </>
          )}
        </div>
      </div>
      <div className="health-card">
        <h3 className="health-card-title" style={{ padding: '1rem 1.25rem' }}>
          基线身体数据
        </h3>
        <p className="health-task-desc" style={{ padding: '0 1rem 0.5rem' }}>
          录入基线日期及各指标值，趋势统计将以此为参考计算变化
        </p>
        <div style={{ padding: '0 1rem 1rem', display: 'grid', gap: '0.75rem' }}>
          <div>
            <label className="health-task-desc">基线日期</label>
            <input
              type="date"
              className="health-input"
              value={profile.baseline?.recordedAt ?? formatDate(new Date())}
              onChange={(e) =>
                saveProfileUpdate({
                  ...profile,
                  baseline: {
                    ...profile.baseline,
                    recordedAt: e.target.value || formatDate(new Date())
                  }
                })
              }
              style={{ maxWidth: 160 }}
            />
            <p className="health-task-desc" style={{ marginTop: 4, fontSize: '0.7rem' }}>
              该日期对应的各指标值，用于趋势对比
            </p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
            <div>
              <label className="health-task-desc">体重(kg)</label>
              <input
                type="number"
                step="0.1"
                className="health-input"
                value={profile.baseline?.weight ?? ''}
                onChange={(e) =>
                  saveProfileUpdate({
                    ...profile,
                    baseline: {
                      ...profile.baseline,
                      weight: parseFloat(e.target.value) || undefined
                    }
                  })
                }
                placeholder="120.6"
              />
            </div>
            <div>
              <label className="health-task-desc">体脂率(%)</label>
              <input
                type="number"
                step="0.1"
                className="health-input"
                value={profile.baseline?.bodyFatPct ?? ''}
                onChange={(e) =>
                  saveProfileUpdate({
                    ...profile,
                    baseline: {
                      ...profile.baseline,
                      bodyFatPct: parseFloat(e.target.value) || undefined
                    }
                  })
                }
                placeholder="33.9"
              />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
            <div>
              <label className="health-task-desc">腰围(cm)</label>
              <input
                type="number"
                className="health-input"
                value={profile.baseline?.waist ?? ''}
                onChange={(e) =>
                  saveProfileUpdate({
                    ...profile,
                    baseline: {
                      ...profile.baseline,
                      waist: parseFloat(e.target.value) || undefined
                    }
                  })
                }
              />
            </div>
            <div>
              <label className="health-task-desc">臀围(cm)</label>
              <input
                type="number"
                className="health-input"
                value={profile.baseline?.hip ?? ''}
                onChange={(e) =>
                  saveProfileUpdate({
                    ...profile,
                    baseline: {
                      ...profile.baseline,
                      hip: parseFloat(e.target.value) || undefined
                    }
                  })
                }
              />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
            <div>
              <label className="health-task-desc">肌肉量(kg)</label>
              <input
                type="number"
                step="0.1"
                className="health-input"
                value={profile.baseline?.muscleMass ?? ''}
                onChange={(e) =>
                  saveProfileUpdate({
                    ...profile,
                    baseline: {
                      ...profile.baseline,
                      muscleMass: parseFloat(e.target.value) || undefined
                    }
                  })
                }
              />
            </div>
            <div>
              <label className="health-task-desc">内脏脂肪等级</label>
              <input
                type="number"
                className="health-input"
                value={profile.baseline?.visceralFatLevel ?? ''}
                onChange={(e) =>
                  saveProfileUpdate({
                    ...profile,
                    baseline: {
                      ...profile.baseline,
                      visceralFatLevel: parseInt(e.target.value, 10) || undefined
                    }
                  })
                }
              />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
            <div>
              <label className="health-task-desc">基础代谢(kcal)</label>
              <input
                type="number"
                className="health-input"
                value={profile.baseline?.bmr ?? ''}
                onChange={(e) =>
                  saveProfileUpdate({
                    ...profile,
                    baseline: {
                      ...profile.baseline,
                      bmr: parseInt(e.target.value, 10) || undefined
                    }
                  })
                }
              />
            </div>
            <div>
              <label className="health-task-desc">代谢年龄(岁)</label>
              <input
                type="number"
                className="health-input"
                value={profile.baseline?.metabolicAge ?? ''}
                onChange={(e) =>
                  saveProfileUpdate({
                    ...profile,
                    baseline: {
                      ...profile.baseline,
                      metabolicAge: parseInt(e.target.value, 10) || undefined
                    }
                  })
                }
              />
            </div>
          </div>
        </div>
      </div>
      <div className="health-card">
        <h3 className="health-card-title" style={{ padding: '1rem 1.25rem' }}>诊断与方案</h3>
        <div style={{ padding: '0 1rem 1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <AlertCircle size={20} color="#ef4444" />
            <span>请遵照医嘱执行饮食与运动方案</span>
          </div>
        </div>
      </div>
      <div className="health-card">
        <h3 className="health-card-title" style={{ padding: '1rem 1.25rem' }}>
          <Bell size={20} style={{ verticalAlign: 'middle', marginRight: 6 }} />
          备注提醒
        </h3>
        <div style={{ padding: '0 1rem 1rem' }}>
          <button
            type="button"
            className="health-diet-plan-link"
            onClick={() => navigate('/health/diet-plan')}
          >
            <div className="health-task-info">
              <BookOpen size={20} color="#10b981" />
              <div>
                <p className="health-task-name">饮食减肥方案</p>
                <p className="health-task-desc">食谱、饮食原则、运动建议</p>
              </div>
            </div>
            <ChevronRight size={20} color="#9ca3af" />
          </button>
        </div>
      </div>
    </div>
  );

  const renderSettings = () => (
    <div style={{ padding: '0 1rem', paddingBottom: '5rem' }}>
      <div className="health-card">
        <h3 className="health-card-title" style={{ padding: '1rem 1.25rem' }}>个人设置</h3>
        <div style={{ padding: '1rem', display: 'grid', gap: '1rem' }}>
          <div>
            <label className="health-task-desc">姓名</label>
            <input
              className="health-input"
              value={profile.name}
              onChange={(e) => saveProfileUpdate({ ...profile, name: e.target.value })}
            />
          </div>
          <div>
            <label className="health-task-desc">年龄</label>
            <input
              type="number"
              className="health-input"
              value={profile.age || ''}
              onChange={(e) =>
                saveProfileUpdate({ ...profile, age: parseInt(e.target.value, 10) || 0 })
              }
            />
          </div>
          <div>
            <label className="health-task-desc">身高 (cm)</label>
            <input
              type="number"
              className="health-input"
              value={profile.height || ''}
              onChange={(e) =>
                saveProfileUpdate({ ...profile, height: parseFloat(e.target.value) || 0 })
              }
            />
          </div>
          <div>
            <label className="health-task-desc">减肥开始日期</label>
            <input
              type="date"
              className="health-input"
              value={profile.startDate || formatDate(new Date())}
              onChange={(e) =>
                saveProfileUpdate({ ...profile, startDate: e.target.value })
              }
            />
          </div>
          <div>
            <label className="health-task-desc">目标体重 (kg)</label>
            <input
              type="number"
              className="health-input"
              value={profile.targetWeight || ''}
              onChange={(e) =>
                saveProfileUpdate({ ...profile, targetWeight: parseFloat(e.target.value) || 0 })
              }
            />
          </div>
          <div>
            <label className="health-task-desc">医生姓名</label>
            <input
              className="health-input"
              value={profile.doctorName || ''}
              onChange={(e) => saveProfileUpdate({ ...profile, doctorName: e.target.value })}
            />
          </div>
          <div>
            <label className="health-task-desc">医院</label>
            <input
              className="health-input"
              value={profile.hospital || ''}
              onChange={(e) => saveProfileUpdate({ ...profile, hospital: e.target.value })}
            />
          </div>
        </div>
      </div>

      <div className="health-card">
        <h3 className="health-card-title" style={{ padding: '1rem 1.25rem' }}>提醒设置</h3>
        <div style={{ padding: '0 1rem 1rem' }}>
          <div className="health-remind-row">
            <div>
              <p className="health-remind-title">服药提醒</p>
              <p className="health-remind-desc">早晚餐前30分钟</p>
            </div>
            <div
              className={`health-toggle ${reminders.medication ? '' : 'off'}`}
              onClick={() => {
                const next = { ...reminders, medication: !reminders.medication };
                setReminders(next);
                saveProfileUpdate({ ...profile, reminders: next });
              }}
            />
          </div>
          <div className="health-remind-row">
            <div>
              <p className="health-remind-title">运动提醒</p>
              <p className="health-remind-desc">每天18:00</p>
            </div>
            <div
              className={`health-toggle ${reminders.exercise ? '' : 'off'}`}
              onClick={() => {
                const next = { ...reminders, exercise: !reminders.exercise };
                setReminders(next);
                saveProfileUpdate({ ...profile, reminders: next });
              }}
            />
          </div>
          <div className="health-remind-row">
            <div>
              <p className="health-remind-title">复诊提醒</p>
              <p className="health-remind-desc">提前1天通知</p>
            </div>
            <div
              className={`health-toggle ${reminders.followUp ? '' : 'off'}`}
              onClick={() => {
                const next = { ...reminders, followUp: !reminders.followUp };
                setReminders(next);
                saveProfileUpdate({ ...profile, reminders: next });
              }}
            />
          </div>
        </div>
      </div>

      <div className="health-card">
        <h3 className="health-card-title" style={{ padding: '1rem 1.25rem' }}>数据管理</h3>
        <div style={{ padding: '1rem' }}>
          <p className="health-task-desc">健康数据已安全存储于服务器</p>
          <p className="health-task-desc" style={{ marginTop: 4 }}>支持多设备同步，随时查看历史记录</p>
        </div>
      </div>

      <div style={{ textAlign: 'center', padding: '1.5rem', color: '#9ca3af', fontSize: '0.875rem' }}>
        <p>健康管家 v1.1（打卡·统计·趋势·活动·方案·设置）</p>
      </div>
    </div>
  );

  const renderActivities = () => {
    if (selectedActivity) {
      const act = selectedActivity;
      const items = act.items || [];
      const metrics = act.metrics || [];
      const addMetric = (m) => {
        if (metrics.some((x) => x.type === m.type)) return;
        setSelectedActivity({ ...act, metrics: [...metrics, m] });
      };
      const removeMetric = (type) => {
        setSelectedActivity({ ...act, metrics: metrics.filter((m) => m.type !== type) });
      };
      const addItem = () => {
        const next = [...items, { name: '', timeSlot: TIME_SLOTS[0], dosage: '', sortOrder: items.length }];
        setSelectedActivity({ ...act, items: next });
      };
      const updateItem = (idx, field, val) => {
        const next = [...items];
        next[idx] = { ...(next[idx] || {}), [field]: val };
        setSelectedActivity({ ...act, items: next });
      };
      const removeItem = (idx) => {
        const next = items.filter((_, i) => i !== idx);
        setSelectedActivity({ ...act, items: next });
      };
      const saveActivityItems = async () => {
        setActivitySaving(true);
        try {
          const payload = {
            name: act.name,
            startDate: editDateRange ? editDateRangeVal.start : act.startDate,
            endDate: editDateRange ? editDateRangeVal.end : act.endDate,
            items: items.filter((it) => (it.name || '').trim()),
            metrics
          };
          const res = await updateActivity(act._id, payload);
          if (res?.success && res?.data) {
            setSelectedActivity(res.data);
            setActivities((prev) =>
              prev.map((a) => (a._id === act._id ? res.data : a))
            );
            setEditDateRange(false);
            setSaveToast(true);
            setTimeout(() => setSaveToast(false), 1500);
          }
        } catch (e) {
          console.error('活动更新失败:', e);
        } finally {
          setActivitySaving(false);
        }
      };
      const backToList = () => {
        setSelectedActivity(null);
        setEditDateRange(false);
      };
      const startEditDateRange = () => {
        setEditDateRangeVal({ start: act.startDate || '', end: act.endDate || '' });
        setEditDateRange(true);
      };
      return (
        <div style={{ padding: '0 1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <button type="button" className="health-btn-sm" onClick={backToList}>
              <ChevronLeft size={18} /> 返回
            </button>
            <h3 className="health-card-title">{act.name}</h3>
            {editDateRange ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <input
                  type="date"
                  className="health-input"
                  value={editDateRangeVal.start}
                  onChange={(e) =>
                    setEditDateRangeVal((p) => ({ ...p, start: e.target.value }))
                  }
                  style={{ width: 130 }}
                />
                <span style={{ color: '#9ca3af' }}>~</span>
                <input
                  type="date"
                  className="health-input"
                  value={editDateRangeVal.end}
                  onChange={(e) =>
                    setEditDateRangeVal((p) => ({ ...p, end: e.target.value }))
                  }
                  style={{ width: 130 }}
                />
                <button
                  type="button"
                  className="health-water-btn"
                  onClick={saveActivityItems}
                  disabled={activitySaving}
                >
                  保存
                </button>
                <button
                  type="button"
                  className="health-btn-sm"
                  onClick={() => setEditDateRange(false)}
                >
                  取消
                </button>
              </div>
            ) : (
              <>
                <span style={{ color: '#9ca3af', fontSize: '0.875rem' }}>
                  {act.startDate} ~ {act.endDate}
                </span>
                <button
                  type="button"
                  className="health-check-btn todo"
                  onClick={startEditDateRange}
                  title="编辑时间区间"
                >
                  <Pencil size={16} />
                </button>
              </>
            )}
          </div>

          <div className="health-card">
            <div className="health-card-header">
              <h4 className="health-card-title">跟踪指标</h4>
              <p style={{ fontSize: '0.8rem', color: '#9ca3af', marginTop: 4 }}>
                选择本活动需要跟踪的指标（数据来自每日打卡）
              </p>
            </div>
            <div style={{ padding: '1rem' }}>
              {metrics.length > 0 && (
                <div style={{ marginBottom: 12, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {metrics.map((m) => (
                    <span
                      key={m.type}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                        padding: '4px 10px',
                        background: '#e0e7ff',
                        color: '#4338ca',
                        borderRadius: 6,
                        fontSize: '0.85rem'
                      }}
                    >
                      {m.label || m.type}
                      {m.unit && <span style={{ opacity: 0.8 }}>({m.unit})</span>}
                      <button
                        type="button"
                        onClick={() => removeMetric(m.type)}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: 0,
                          marginLeft: 4
                        }}
                        title="移除"
                      >
                        <Trash2 size={14} color="#ef4444" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {TRACKING_METRICS.filter((m) => !metrics.some((x) => x.type === m.type)).map(
                  (m) => (
                    <button
                      key={m.type}
                      type="button"
                      className="health-ketone-btn"
                      onClick={() => addMetric(m)}
                      style={{ fontSize: '0.8rem' }}
                    >
                      <Plus size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                      {m.label}
                    </button>
                  )
                )}
              </div>
            </div>
          </div>

          <div className="health-card">
            <div className="health-card-header">
              <h4 className="health-card-title">打卡事项管理</h4>
              <p style={{ fontSize: '0.8rem', color: '#9ca3af', marginTop: 4 }}>
                按每次服用添加事项，例如早+晚需打两次卡
              </p>
            </div>
            <div style={{ padding: '1rem' }}>
              {items.map((it, idx) => (
                <div key={idx} className="health-item-row" style={{ marginBottom: 12 }}>
                  <input
                    className="health-input"
                    placeholder="名称（如药物名）"
                    value={it.name || ''}
                    onChange={(e) => updateItem(idx, 'name', e.target.value)}
                    style={{ flex: 1, marginRight: 8 }}
                  />
                  <select
                    className="health-input"
                    value={it.timeSlot || TIME_SLOTS[0]}
                    onChange={(e) => updateItem(idx, 'timeSlot', e.target.value)}
                    style={{ width: 100, marginRight: 8 }}
                  >
                    {TIME_SLOTS.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                  <input
                    className="health-input"
                    placeholder="剂量"
                    value={it.dosage || ''}
                    onChange={(e) => updateItem(idx, 'dosage', e.target.value)}
                    style={{ width: 80, marginRight: 8 }}
                  />
                  <button
                    type="button"
                    className="health-check-btn todo"
                    onClick={() => removeItem(idx)}
                    title="删除"
                  >
                    <Trash2 size={16} color="#ef4444" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                className="health-ketone-btn"
                onClick={addItem}
              >
                <Plus size={16} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                添加打卡事项
              </button>
              <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
                <button
                  type="button"
                  className="health-water-btn"
                  onClick={saveActivityItems}
                  disabled={activitySaving}
                >
                  {activitySaving ? '保存中...' : '保存'}
                </button>
                <button
                  type="button"
                  className="health-btn-sm"
                  onClick={backToList}
                >
                  返回列表
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }
    if (activityFormOpen) {
      const submitCreate = async () => {
        const end = activityFormData.endDate || activityFormData.startDate;
        if (!activityFormData.name?.trim()) return;
        setActivitySaving(true);
        try {
          const res = await createActivity({
            name: activityFormData.name.trim(),
            startDate: activityFormData.startDate,
            endDate: end
          });
          if (res?.success && res?.data) {
            setActivities((prev) => [res.data, ...prev]);
            setActivityFormOpen(false);
            setActivityFormData({ name: '', startDate: formatDate(new Date()), endDate: '' });
            setSaveToast(true);
            setTimeout(() => setSaveToast(false), 1500);
            setSelectedActivity(res.data);
          }
        } catch (e) {
          console.error('创建活动失败:', e);
        } finally {
          setActivitySaving(false);
        }
      };
      return (
        <div style={{ padding: '0 1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <button
              type="button"
              className="health-btn-sm"
              onClick={() => setActivityFormOpen(false)}
            >
              <ChevronLeft size={18} /> 返回
            </button>
            <h3 className="health-card-title">新建打卡活动</h3>
          </div>
          <div className="health-card">
            <div style={{ padding: '1rem' }}>
              <div style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', marginBottom: 4, fontSize: '0.9rem' }}>
                  活动名称
                </label>
                <input
                  className="health-input"
                  placeholder="例如：药物"
                  value={activityFormData.name}
                  onChange={(e) =>
                    setActivityFormData((p) => ({ ...p, name: e.target.value }))
                  }
                />
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', marginBottom: 4, fontSize: '0.9rem' }}>
                  开始日期
                </label>
                <input
                  type="date"
                  className="health-input"
                  value={activityFormData.startDate}
                  onChange={(e) =>
                    setActivityFormData((p) => ({ ...p, startDate: e.target.value }))
                  }
                />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 4, fontSize: '0.9rem' }}>
                  结束日期
                </label>
                <input
                  type="date"
                  className="health-input"
                  value={activityFormData.endDate}
                  onChange={(e) =>
                    setActivityFormData((p) => ({ ...p, endDate: e.target.value }))
                  }
                />
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  type="button"
                  className="health-water-btn"
                  onClick={submitCreate}
                  disabled={activitySaving}
                >
                  {activitySaving ? '创建中...' : '创建并添加事项'}
                </button>
                <button
                  type="button"
                  className="health-btn-sm"
                  onClick={() => setActivityFormOpen(false)}
                >
                  取消
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }
    if (activitiesLoading) {
      return (
        <div style={{ padding: '2rem', textAlign: 'center', color: '#9ca3af' }}>
          加载中...
        </div>
      );
    }
    const handleResetAll = async () => {
      if (activities.length === 0) return;
      if (!window.confirm('确定清空所有打卡活动及完成记录？此操作不可恢复。')) return;
      try {
        setActivitiesLoading(true);
        const res = await resetActivities();
        if (res?.success) {
          setActivities([]);
          setSelectedActivity(null);
          setActivityFormOpen(false);
          setHistoryActivities([]);
          setHistoryCompletions([]);
          setActivitiesInRange([]);
          setActivityCompletions([]);
          setSaveToast(true);
          setTimeout(() => setSaveToast(false), 1500);
        }
      } catch (e) {
        console.error('重置活动失败:', e);
      } finally {
        setActivitiesLoading(false);
      }
    };
    return (
      <div style={{ padding: '0 1rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 className="health-card-title">打卡活动</h3>
          <div style={{ display: 'flex', gap: 8 }}>
            {activities.length > 0 && (
              <button
                type="button"
                className="health-btn-sm"
                style={{ color: '#ef4444', borderColor: '#ef4444' }}
                onClick={handleResetAll}
                disabled={activitiesLoading}
              >
                清空所有活动
              </button>
            )}
            <button
              type="button"
              className="health-water-btn"
              onClick={() => {
                setActivityFormData({
                  name: '',
                  startDate: formatDate(new Date()),
                  endDate: getDefaultEndDate()
                });
                setActivityFormOpen(true);
              }}
            >
              <Plus size={16} style={{ marginRight: 4, verticalAlign: 'middle' }} />
              新建活动
            </button>
          </div>
        </div>
        <div className="health-card">
          <div style={{ padding: '1rem' }}>
            {activities.length === 0 ? (
              <p style={{ color: '#9ca3af', textAlign: 'center', padding: '2rem' }}>
                暂无活动，点击「新建活动」创建
              </p>
            ) : (
              activities.map((a) => (
                <div
                  key={a._id}
                  className="health-item-row"
                  style={{
                    marginBottom: 12,
                    padding: 12,
                    borderRadius: 8,
                    background: '#f9fafb',
                    cursor: 'pointer'
                  }}
                  onClick={() => setSelectedActivity(a)}
                  onKeyDown={(e) => e.key === 'Enter' && setSelectedActivity(a)}
                  role="button"
                  tabIndex={0}
                >
                  <div style={{ flex: 1 }}>
                    <p className="health-item-name">{a.name}</p>
                    <p className="health-item-meta">
                      {a.startDate} ~ {a.endDate}
                      {(a.items || []).length > 0 && ` · ${a.items.length} 项`}
                    </p>
                  </div>
                  <div style={{ display: 'flex', gap: 4 }} onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      className="health-check-btn todo"
                      onClick={() => {
                        if (window.confirm('确定删除该活动？')) {
                          deleteActivity(a._id).then(() => {
                            setActivities((prev) => prev.filter((x) => x._id !== a._id));
                            setSaveToast(true);
                            setTimeout(() => setSaveToast(false), 1500);
                          });
                        }
                      }}
                      title="删除"
                    >
                      <Trash2 size={16} color="#ef4444" />
                    </button>
                    <ChevronRight size={18} color="#9ca3af" />
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'stats':
        return renderStats();
      case 'trends':
        return renderTrends();
      case 'activities':
        return renderActivities();
      case 'care':
        return renderCarePlan();
      case 'settings':
        return renderSettings();
      default:
        return renderDashboard();
    }
  };

  const toggleSidebar = () => {
    if (window.innerWidth < 768) {
      setSidebarOverlayOpen((o) => !o);
    } else {
      setSidebarCollapsed((s) => !s);
    }
  };
  const closeOverlay = () => setSidebarOverlayOpen(false);
  const selectTab = (id) => {
    setActiveTab(id);
    if (window.innerWidth < 768) closeOverlay();
  };

  const sidebarEl = (
    <aside
      className={`health-sidebar ${sidebarCollapsed && !sidebarOverlayOpen ? 'health-sidebar-collapsed' : ''} ${sidebarOverlayOpen ? 'health-sidebar-overlay' : ''}`}
    >
      <div className="health-sidebar-header">
        {(!sidebarCollapsed || sidebarOverlayOpen) && <span>健康管家</span>}
        <button
          className="health-sidebar-toggle"
          onClick={sidebarOverlayOpen ? closeOverlay : toggleSidebar}
          aria-label={sidebarCollapsed ? '展开侧栏' : '收缩侧栏'}
          title={sidebarCollapsed ? '展开' : '收缩'}
        >
          {sidebarOverlayOpen ? (
            <ChevronLeft size={18} />
          ) : sidebarCollapsed ? (
            <ChevronRight size={18} />
          ) : (
            <ChevronLeft size={18} />
          )}
        </button>
      </div>
      <nav className="health-sidebar-nav">
        {navItems.map((item) => (
          <button
            key={item.id}
            className={`health-sidebar-item ${activeTab === item.id ? 'active' : ''}`}
            onClick={() => selectTab(item.id)}
            title={sidebarCollapsed && !sidebarOverlayOpen ? item.label : undefined}
          >
            <item.icon size={20} />
            {(!sidebarCollapsed || sidebarOverlayOpen) && <span>{item.label}</span>}
          </button>
        ))}
      </nav>
    </aside>
  );

  const showMenuBtn = isMobile && sidebarCollapsed && !sidebarOverlayOpen;

  return (
    <div className="health-page">
      {saveToast && (
        <div className="health-save-toast">已保存</div>
      )}
      {sidebarOverlayOpen && (
        <div
          className="health-sidebar-backdrop"
          onClick={closeOverlay}
          aria-hidden="true"
        />
      )}
      {createPortal(sidebarEl, document.body)}
      <div
        className="health-main-wrap"
        style={{
          marginLeft:
            isMobile && (sidebarCollapsed && !sidebarOverlayOpen) ? 0 : sidebarCollapsed ? 56 : 240
        }}
      >
        <div className="health-top-bar">
          {showMenuBtn && (
            <button
              type="button"
              className="health-menu-btn"
              onClick={() => setSidebarOverlayOpen(true)}
              aria-label="打开菜单"
            >
              <Menu size={24} />
            </button>
          )}
          <h1 className="health-top-title">健康管家</h1>
          <div className="health-user-info">
            <span>{profile.name}</span>
            <div className="health-user-avatar">{profile.name?.charAt(0) || '用'}</div>
          </div>
        </div>

        <div style={{ padding: '1rem' }}>
          {renderContent()}
        </div>
      </div>
    </div>
  );
}

export default HealthHome;
